import React, { useState } from 'react'
import styled from 'styled-components'
import tw from 'twin.macro'
import { InjectedModalProps, Modal, useResizeObserver } from '@nominex/dex-uikit'
import { SlSwitch } from '@shoelace-style/shoelace/dist/react'
import {
  useAudioModeManager,
  useExpertModeManager,
  useUserExpertModeAcknowledgementShow,
  useUserSingleHopOnly,
} from 'state/user/hooks'
import { useTranslation } from 'contexts/Localization'
import { useSwapActionHandlers } from 'state/swap/hooks'
import QuestionHelper from 'components/QuestionHelper'
import TransactionSettings from './TransactionSettings'
import ExpertModal from './ExpertModal'

const ScrollableContainer = styled.div`
  ${tw`flex flex-col max-h-[400px]`}

  ${({ theme }) => theme.mediaQueries.sm} {
    max-height: none;
  }
`

const SettingsModal: React.FC<InjectedModalProps> = ({ onDismiss }) => {
  const [showConfirmExpertModal, setShowConfirmExpertModal] = useState(false)
  const [showExpertModeAcknowledgement, setShowExpertModeAcknowledgement] = useUserExpertModeAcknowledgementShow()
  const [expertMode, toggleExpertMode] = useExpertModeManager()
  const [singleHopOnly, setSingleHopOnly] = useUserSingleHopOnly()
  const [audioPlay, toggleSetAudioMode] = useAudioModeManager()
  const { onChangeRecipient } = useSwapActionHandlers()
  const { ref, refWidth } = useResizeObserver()
  const isMobileView = refWidth < 445

  const { t } = useTranslation()

  if (showConfirmExpertModal) {
    return (
      <ExpertModal
        setShowConfirmExpertModal={setShowConfirmExpertModal}
        onDismiss={onDismiss}
        setShowExpertModeAcknowledgement={setShowExpertModeAcknowledgement}
      />
    )
  }

  const handleExpertModeToggle = () => {
    if (expertMode) {
      onChangeRecipient(null)
      toggleExpertMode()
    } else if (!showExpertModeAcknowledgement) {
      onChangeRecipient(null)
      toggleExpertMode()
    } else {
      setShowConfirmExpertModal(true)
    }
  }

  return (
    <Modal title={t('Settings')} onDismiss={onDismiss} maxWidth="460px">
      <ScrollableContainer ref={ref}>
        <div className="flex flex-col pb-24">
          <TransactionSettings isMobileView={isMobileView} />
        </div>
        <div className="flex flex-col gap-24 py-24 border-t border-t-dark-gray-400">
          <SlSwitch id="toggle-expert-mode-button" checked={expertMode} onSlChange={handleExpertModeToggle}>
            <div className="flex items-center ml-12">
              <p className="text-white text-14 font-medium">{t('Expert Mode')}</p>
              <QuestionHelper
                content={t('Bypasses confirmation modals and allows high slippage trades. Use at your own risk.')}
                placement="top-start"
              />
            </div>
          </SlSwitch>
          <SlSwitch
            id="toggle-disable-multihop-button"
            checked={singleHopOnly}
            onSlChange={() => {
              setSingleHopOnly(!singleHopOnly)
            }}
          >
            <div className="flex items-center ml-12">
              <p className="text-white text-14 font-medium">{t('Disable Multihops')}</p>
              <QuestionHelper content={t('Restricts swaps to direct pairs only.')} placement="top-start" />
            </div>
          </SlSwitch>
          <SlSwitch checked={audioPlay} onSlChange={toggleSetAudioMode}>
            <div className="flex items-center ml-12">
              <p className="text-white text-14 font-medium">{t('Flippy sounds')}</p>
              <QuestionHelper
                content={t('Fun sounds to make a truly immersive trading experience')}
                placement="top-start"
              />
            </div>
          </SlSwitch>
        </div>
      </ScrollableContainer>
    </Modal>
  )
}

export default SettingsModal
